// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The satellites' own clock. It opens on "now" at real speed, and the
 * viewer can scrub up to a day either way and play faster (the owner,
 * 2026-10-22: "live, scrub ±1 day"). Beyond a day the mean elements drift
 * too far from the truth to be worth drawing.
 */
export const SCRUB_LIMIT_MS = 24 * 3600 * 1000

/** Speeds offered, sim seconds per real second. */
export const SATELLITE_SPEEDS = [1, 10, 60, 300, 1200] as const

export interface SatelliteClockState {
  /** True while following real time at 1×. */
  live: boolean
  playing: boolean
  speed: number
  /** Sim time at `anchorRealMs`. */
  anchorSimMs: number
  anchorRealMs: number
}

export class SatelliteClock {
  private state: SatelliteClockState
  private readonly now: () => number
  private listeners = new Set<() => void>()

  constructor(now: () => number = () => Date.now()) {
    this.now = now
    const t = now()
    this.state = { live: true, playing: true, speed: 1, anchorSimMs: t, anchorRealMs: t }
  }

  /** Current sim time, clamped to the ±1 day window around real now. */
  time(): number {
    const real = this.now()
    const s = this.state
    if (s.live) return real
    const raw = s.playing ? s.anchorSimMs + (real - s.anchorRealMs) * s.speed : s.anchorSimMs
    const lo = real - SCRUB_LIMIT_MS
    const hi = real + SCRUB_LIMIT_MS
    if (raw > hi || raw < lo) {
      // Played into the edge: stop there rather than run off the window.
      const edge = Math.min(hi, Math.max(lo, raw))
      this.state = { ...s, playing: false, anchorSimMs: edge, anchorRealMs: real }
      this.emit()
      return edge
    }
    return raw
  }

  get live(): boolean {
    return this.state.live
  }
  get playing(): boolean {
    return this.state.live || this.state.playing
  }
  get speed(): number {
    return this.state.live ? 1 : this.state.speed
  }

  /** Offset of the sim time from real now, ms (0 when live). */
  offsetMs(): number {
    return this.time() - this.now()
  }

  goLive(): void {
    const t = this.now()
    this.state = { live: true, playing: true, speed: 1, anchorSimMs: t, anchorRealMs: t }
    this.emit()
  }

  /** Jumps to `simMs` (clamped to the window), keeping play/pause and speed. */
  seek(simMs: number): void {
    const real = this.now()
    const t = Math.min(real + SCRUB_LIMIT_MS, Math.max(real - SCRUB_LIMIT_MS, simMs))
    this.state = { ...this.state, live: false, anchorSimMs: t, anchorRealMs: real }
    this.emit()
  }

  setPlaying(playing: boolean): void {
    const t = this.time()
    this.state = { ...this.state, live: false, playing, anchorSimMs: t, anchorRealMs: this.now() }
    this.emit()
  }

  setSpeed(speed: number): void {
    const t = this.time()
    const live = this.state.live && speed === 1
    this.state = { ...this.state, live, playing: true, speed, anchorSimMs: t, anchorRealMs: this.now() }
    this.emit()
  }

  /** For rooms: the state to send, and to apply on the other side. */
  snapshot(): SatelliteClockState {
    return { ...this.state }
  }
  apply(state: SatelliteClockState): void {
    this.state = { ...state }
    this.emit()
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  private emit(): void {
    for (const fn of this.listeners) fn()
  }
}
